const DEFAULT_PATTERN = /^DEFAULT\s+\[(@?[A-Z0-9_#]+)\]\s*=\s*(.*)$/i;
const DEFAULT_START_PATTERN = /\bDEFAULT\s+\[(@?[A-Z0-9_#]+)\]\s*=/gi;

function parseTechnicalSuffix(variableName = '') {
  const match = variableName.match(/^(.*?)(#[A-Z])$/i);

  return match
    ? {
        name: match[1],
        suffix: match[2].toUpperCase(),
      }
    : {
        name: variableName,
        suffix: null,
      };
}

function createDefaultDiagnostic(code, message) {
  return {
    level: 'warning',
    code,
    message,
  };
}

function stripOuterQuotes(value = '') {
  const trimmed = value.trim();

  if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
    return trimmed.slice(1, -1);
  }

  return trimmed;
}

function cleanCatalogSource(rawValue = '') {
  return rawValue.replace(/\+\s*/g, '').replace(/["']/g, '').trim();
}

function cleanOptionText(value = '') {
  return value.trim().replace(/^["']+|["']+$/g, '').trim();
}

function parseCatalogOptions(rawValue = '') {
  return cleanCatalogSource(rawValue)
    .split('|')
    .map((option) => {
      const separatorIndex = option.indexOf('=');

      if (separatorIndex === -1) {
        return null;
      }

      const label = cleanOptionText(option.slice(0, separatorIndex));
      const value = cleanOptionText(option.slice(separatorIndex + 1));

      if (!label || !value) {
        return null;
      }

      return {
        label,
        value,
      };
    })
    .filter(Boolean);
}

function parseDefaultValue(rawValue = '') {
  const value = rawValue.trim().replace(/\+\s*$/g, '').trim();
  const variableMatch = value.match(/^\[(@?[A-Z0-9_#]+)\]$/i);

  if (variableMatch) {
    const variableInfo = parseTechnicalSuffix(variableMatch[1]);

    return {
      value: variableInfo.name,
      valueSuffix: variableInfo.suffix,
      valueType: 'variable',
    };
  }

  const literalValue = stripOuterQuotes(value);

  if (/^TRUE$/i.test(literalValue)) {
    return {
      value: true,
      valueType: 'boolean',
    };
  }

  if (/^FALSE$/i.test(literalValue)) {
    return {
      value: false,
      valueType: 'boolean',
    };
  }

  return {
    value: literalValue,
    valueType: 'literal',
  };
}

function normalizeVariableName(variableName = '') {
  return variableName.toUpperCase();
}

function isSelfAssignment(targetInfo, parsedValue) {
  return (
    parsedValue.valueType === 'variable' &&
    normalizeVariableName(targetInfo.name) === normalizeVariableName(parsedValue.value) &&
    (targetInfo.suffix || null) === (parsedValue.valueSuffix || null)
  );
}

export function parseDefaultInstruction(raw = '') {
  const match = raw.match(DEFAULT_PATTERN);

  if (!match) {
    return {
      type: 'default',
      defaultType: 'unknown',
      visible: false,
      parseStatus: 'unknown',
      raw,
      diagnostic: createDefaultDiagnostic('DEFAULT_PARSE_UNCERTAIN', 'DEFAULT no pudo interpretarse completamente'),
    };
  }

  const targetInfo = parseTechnicalSuffix(match[1]);
  const rawValue = match[2].trim();
  const catalogOptions = targetInfo.suffix === '#D' ? parseCatalogOptions(rawValue) : [];
  const parsedValue = parseDefaultValue(rawValue);
  const base = {
    type: 'default',
    target: targetInfo.name,
    visible: false,
    parseStatus: 'parsed',
    raw,
  };

  if (targetInfo.suffix === '#D' && parsedValue.valueType === 'literal' && /^CSV:/i.test(String(parsedValue.value))) {
    const source = String(parsedValue.value);
    return {
      ...base,
      defaultType: 'default_external_catalog',
      suffix: '#D',
      catalogId: `${targetInfo.name}Catalog`,
      source,
      path: source.replace(/^CSV:/i, ''),
      options: [],
    };
  }

  if (targetInfo.suffix === '#D' && catalogOptions.length > 0) {
    return {
      ...base,
      defaultType: 'default_catalog',
      suffix: '#D',
      catalogId: `${targetInfo.name}Catalog`,
      options: catalogOptions,
    };
  }

  if (targetInfo.suffix === '#C' && parsedValue.valueType === 'boolean') {
    return {
      ...base,
      defaultType: 'default_checkbox',
      suffix: '#C',
      value: parsedValue.value,
      valueType: 'boolean',
    };
  }

  if (parsedValue.valueType === 'variable') {
    return {
      ...base,
      defaultType: 'default_assignment',
      targetSuffix: targetInfo.suffix,
      value: parsedValue.value,
      valueSuffix: parsedValue.valueSuffix,
      valueType: 'variable',
      isSelfAssignment: isSelfAssignment(targetInfo, parsedValue),
    };
  }

  return {
    ...base,
    defaultType: 'default_value',
    targetSuffix: targetInfo.suffix,
    value: parsedValue.value,
    valueType: parsedValue.valueType,
  };
}

export function splitDefaultInstructions(raw = '') {
  const source = String(raw ?? '').trim();

  if (!source) {
    return [];
  }

  const matches = [...source.matchAll(DEFAULT_START_PATTERN)];

  if (matches.length <= 1) {
    return [source];
  }

  return matches
    .map((match, index) => source.slice(match.index, matches[index + 1]?.index ?? source.length).trim())
    .filter(Boolean);
}

export function parseDefaultInstructions(raw = '') {
  return splitDefaultInstructions(raw).map((instruction) => parseDefaultInstruction(instruction));
}
